import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiHome, FiBook, FiVideo, FiAward, FiUser, FiPlay, FiDownload, 
  FiStar, FiClock, FiCheckCircle, FiArrowLeft, FiLock, FiFileText,
  FiChevronDown, FiChevronUp, FiCheck, FiFlag, FiTarget, FiXCircle, FiDollarSign
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import EducationalParticles from '../../components/common/EducationalParticles';
import VideoViewer from '../../components/common/VideoViewer';
import FileViewer from '../../components/common/FileViewer';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PurchaseCourseModal from '../../components/common/PurchaseCourseModal';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { useCourseOwnership } from '../../contexts/CourseOwnershipContext';
import { 
  learnerCourseAPI,
  learnerDownloadAPI,
  learnerProgressAPI,
} from '../../api/learnerEndpoints';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import { 
  DashboardContainer, 
  EmptyState,
} from '../dashboards/Dashboard.styles';
import {
  CourseDetailWrapper,
  CourseHeader,
  CourseHeaderContent,
  CourseThumbnailLarge,
  CourseTitle,
  CourseInstructor,
  CourseRating,
  CourseStats,
  CourseMainContent,
  CourseContentPanel,
  CourseVideoPanel,
  CourseSidebar,
  SectionHeader,
  SectionItem,
  SectionItemHeader,
  SectionItemContent,
  LessonItem,
  LessonIcon,
  LessonTitle,
  LessonDuration,
  CompletionCheckbox,
  CourseAboutSection,
  CourseInfoGrid,
  CourseInfoItem,
  CourseDescription,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressText,
  VideoPlayerSection,
  CourseContentSection,
} from './CourseDetailPage.styles';

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { ownsCourse, handlePurchaseRequired, handlePurchaseSuccess } = useCourseOwnership();
  const [loading, setLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [course, setCourse] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [contentItems, setContentItems] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [lastProgressUpdate, setLastProgressUpdate] = useState({});
  const [pendingProgressUpdates, setPendingProgressUpdates] = useState({});
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Video tracking state (simulated)
  const [videoProgress, setVideoProgress] = useState({});
  const [completedItems, setCompletedItems] = useState(new Set());
  
  const [ratingForm, setRatingForm] = useState({
    rating: 5,
    review: '',
  });
  
  const [flagForm, setFlagForm] = useState({
    reason: '',
  });

  const menuItems = getLearnerMenu();

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails();
      loadProgressData();
    }
  }, [courseId]);

  // Send pending updates when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Send pending updates synchronously before page unload
      sendPendingProgressUpdates();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Send updates when user switches tabs or minimizes
        sendPendingProgressUpdates();
      }
    };

    // Periodic backup every 2 minutes
    const backupInterval = setInterval(() => {
      sendPendingProgressUpdates();
    }, 120000); // 2 minutes

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(backupInterval);
    };
  }, [pendingProgressUpdates]);

  // Build flattened content items list (videos + their attached files)
  useEffect(() => {
    if (course && course.videos) {
      const items = [];
      
      // Sort videos by order_index
      const sortedVideos = [...course.videos].sort((a, b) => a.order_index - b.order_index);
      
      sortedVideos.forEach(video => {
        // Add video
        items.push({
          type: 'video',
          data: video,
          id: video.id
        });
        
        // Add attached files immediately after their video
        const attachedFiles = course.files?.filter(f => f.course_video_id === video.id) || [];
        attachedFiles.forEach(file => {
          items.push({
            type: 'file',
            data: file,
            id: file.id,
            parentVideoId: video.id
          });
        });
      });
      
      setContentItems(items);
    }
  }, [course]);

  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      const [courseResponse, progressResponse] = await Promise.all([
        learnerCourseAPI.getCourseDetails(courseId),
        learnerProgressAPI.getCourseProgress(courseId).catch(() => null) // Graceful fallback
      ]);
      
      const courseData = courseResponse.data.course;
      
      // If progress data is available, merge it with course data
      if (progressResponse?.data) {
        const progressData = progressResponse.data;
        
        // Update course with overall progress
        courseData.overall_progress = progressData.overall_progress || 0;
        courseData.total_items = progressData.total_items || 0;
        courseData.completed_count = progressData.completed_count || 0;
        
        // Update videos with progress data
        if (progressData.video_progress) {
          courseData.videos = courseData.videos?.map(video => {
            const videoProgress = progressData.video_progress.find(vp => vp.video_id === video.id);
            return {
              ...video,
              progress: videoProgress ? {
                current_time: videoProgress.current_time,
                duration: videoProgress.duration,
                progress_percentage: videoProgress.progress_percentage,
                last_watched_at: videoProgress.last_watched_at,
                completed: videoProgress.completed
              } : null
            };
          }) || [];
        }
        
        // Update files with completion data
        if (progressData.completed_items) {
          courseData.files = courseData.files?.map(file => {
            const fileCompletion = progressData.completed_items.find(ci => 
              ci.item_type === 'file' && ci.item_id === file.id
            );
            return {
              ...file,
              completed: !!fileCompletion,
              completed_at: fileCompletion?.completed_at || null
            };
          }) || [];
        }
        
        // Update quizzes with completion data
        if (progressData.completed_items) {
          courseData.quizzes = courseData.quizzes?.map(quiz => {
            const quizCompletion = progressData.completed_items.find(ci => 
              ci.item_type === 'quiz' && ci.item_id === quiz.id
            );
            return {
              ...quiz,
              completed: !!quizCompletion,
              completed_at: quizCompletion?.completed_at || null
            };
          }) || [];
        }
        
        // Build initial completedItems set from API data
        const initialCompleted = new Set();
        
        // Add completed items (files, quizzes, etc.)
        if (progressData.completed_items) {
          progressData.completed_items.forEach(item => {
            initialCompleted.add(`${item.item_type}_${item.item_id}`);
          });
        }
        
        // Add completed videos from video_progress
        if (progressData.video_progress) {
          progressData.video_progress.forEach(videoProgress => {
            if (videoProgress.completed) {
              initialCompleted.add(`video_${videoProgress.video_id}`);
            }
          });
        }
        
        setCompletedItems(initialCompleted);
      }
      
      setCourse(courseData);
      
      // Auto-expand first section and select first video
      if (courseData.videos && courseData.videos.length > 0) {
        setExpandedSections({ 'videos': true });
        // Auto-select first video for embedded player
        setSelectedVideo(courseData.videos[0]);
        setCurrentItemIndex(0);
      }
    } catch (error) {
      if (error.response?.status === 403 && (error.response?.data?.purchase_required || error.response?.data?.message)) {
        showToast('warning', error.response?.data?.message || 'You need to purchase this course to access its content.');
        // Course details might still be accessible for preview
        if (error.response?.data?.course) {
          setCourse(error.response.data.course);
        } else {
          navigate('/learner/browse');
        }
      } else {
        console.error('Error fetching course details:', error);
        showToast('error', 'Failed to load course details');
        navigate('/learner/courses');
      }
    } finally {
      setLoading(false);
    }
  };

  // Simulated progress loading
  const loadProgressData = () => {
    const savedProgress = localStorage.getItem(`course_progress_${courseId}`);
    const savedCompleted = localStorage.getItem(`course_completed_${courseId}`);
    
    if (savedProgress) {
      setVideoProgress(JSON.parse(savedProgress));
    }
    if (savedCompleted) {
      setCompletedItems(new Set(JSON.parse(savedCompleted)));
    }
  };

  // Save progress to local storage (will be replaced with API call)
  const saveProgressData = (progress, completed) => {
    localStorage.setItem(`course_progress_${courseId}`, JSON.stringify(progress));
    localStorage.setItem(`course_completed_${courseId}`, JSON.stringify(Array.from(completed)));
  };

  const handleWatchVideo = (video) => {
    if (!ownsCourse(courseId)) {
      showToast('warning', 'You need to purchase this course to watch videos.');
      setShowPurchaseModal(true);
      return;
    }
    
    // Find the index of this video in contentItems
    const index = contentItems.findIndex(item => item.type === 'video' && item.id === video.id);
    if (index !== -1) {
      setCurrentItemIndex(index);
    }
    
    // Simply set the selected video - it will be displayed in the embedded player
    setSelectedVideo(video);
    
    // Scroll to video player section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePreviewFile = (file) => {
    if (!ownsCourse(courseId)) {
      showToast('warning', 'You need to purchase this course to preview files.');
      setShowPurchaseModal(true);
      return;
    }
    
    // Find the index of this file in contentItems
    const index = contentItems.findIndex(item => item.type === 'file' && item.id === file.id);
    if (index !== -1) {
      setCurrentItemIndex(index);
    }
    
    setSelectedFile(file);
    setShowFileViewer(true);
  };

  const handleNextContent = () => {
    if (currentItemIndex < contentItems.length - 1) {
      const nextIndex = currentItemIndex + 1;
      const nextItem = contentItems[nextIndex];
      
      setIsTransitioning(true);
      setCurrentItemIndex(nextIndex);
      
      // Close file viewer if open
      setShowFileViewer(false);
      setSelectedFile(null);
      
      // Small delay for smooth transition
      setTimeout(() => {
        if (nextItem.type === 'video') {
          setSelectedVideo(nextItem.data);
        } else if (nextItem.type === 'file') {
          setSelectedFile(nextItem.data);
          setShowFileViewer(true);
        }
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handlePreviousContent = () => {
    if (currentItemIndex > 0) {
      const prevIndex = currentItemIndex - 1;
      const prevItem = contentItems[prevIndex];
      
      setIsTransitioning(true);
      setCurrentItemIndex(prevIndex);
      
      // Close file viewer if open
      setShowFileViewer(false);
      setSelectedFile(null);
      
      // Small delay for smooth transition
      setTimeout(() => {
        if (prevItem.type === 'video') {
          setSelectedVideo(prevItem.data);
        } else if (prevItem.type === 'file') {
          setSelectedFile(prevItem.data);
          setShowFileViewer(true);
        }
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handleDownload = async (resourceType, resourceId) => {
    try {
      const response = await learnerDownloadAPI.getDownloadUrl(resourceType, resourceId);
      
      if (response.data.download_url) {
        const testResponse = await fetch(response.data.download_url, { method: 'HEAD' });
        if (testResponse.ok) {
          window.open(response.data.download_url, '_blank');
        } else {
          showToast('error', 'Download link is not accessible. Please try again later.');
        }
      } else {
        showToast('error', 'No download URL available');
      }
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 403) {
        if (error.response?.data?.purchase_required) {
          showToast('warning', error.response?.data?.message || 'Course purchase required to download files.');
          setShowPurchaseModal(true);
        } else {
          showToast('error', 'Access denied. Please contact support.');
        }
      } else if (error.response?.status === 404) {
        showToast('error', 'File not found or not downloadable.');
      } else {
        showToast('error', 'Failed to get download link. Please try again later.');
      }
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const toggleItemCompletion = async (itemType, itemId) => {
    const isCurrentlyCompleted = completedItems.has(`${itemType}_${itemId}`);
    const newCompleted = !isCurrentlyCompleted;
    
    setCompletedItems(prev => {
      const newSet = new Set(prev);
      const key = `${itemType}_${itemId}`;
      
      if (newCompleted) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      
      saveProgressData(videoProgress, newSet);
      return newSet;
    });
    
    // Send to backend
    try {
      await learnerProgressAPI.toggleCompletion(itemType, itemId, newCompleted);
    } catch (error) {
      console.log('Failed to update completion status:', error);
      // Revert on error
      setCompletedItems(prev => {
        const revertedSet = new Set(prev);
        const key = `${itemType}_${itemId}`;
        if (newCompleted) {
          revertedSet.delete(key);
        } else {
          revertedSet.add(key);
        }
        return revertedSet;
      });
    }
    
    showToast('success', newCompleted ? 'Marked as complete' : 'Marked as incomplete');
  };

  const handleVideoProgress = (videoId, currentTime, duration) => {
    // Only process if we have a valid video ID
    if (!videoId || videoId === 'unknown') {
      console.warn('Invalid video ID for progress tracking:', videoId);
      return;
    }

    const progressPercentage = (currentTime / duration) * 100;
    const isCompleted = progressPercentage >= 95; // Auto-complete at 95% as per API docs
    
    setVideoProgress(prev => {
      const newProgress = {
        ...prev,
        [videoId]: {
          currentTime,
          duration,
          progress: progressPercentage
        }
      };
      saveProgressData(newProgress, completedItems);
      return newProgress;
    });

    // Store progress locally for batch sending later
    setPendingProgressUpdates(prev => ({
      ...prev,
      [videoId]: {
        current_time: currentTime,
        duration: duration,
        progress_percentage: progressPercentage,
        completed: isCompleted,
        timestamp: Date.now()
      }
    }));

    // Immediately mark as completed in UI if progress is sufficient
    if (isCompleted) {
      setCompletedItems(prev => {
        const newSet = new Set(prev);
        newSet.add(`video_${videoId}`);
        return newSet;
      });
    }
  };

  // Send all pending progress updates to backend
  const sendPendingProgressUpdates = async () => {
    const updates = Object.entries(pendingProgressUpdates).filter(([videoId]) => 
      videoId && videoId !== 'unknown'
    );
    
    if (updates.length === 0) return;

    try {
      // Send all updates in parallel
      await Promise.all(
        updates.map(([videoId, progressData]) =>
          learnerProgressAPI.updateVideoProgress(videoId, progressData)
        )
      );
      
      // Clear pending updates after successful send
      setPendingProgressUpdates({});
      console.log(`Sent ${updates.length} progress updates to backend`);
    } catch (error) {
      console.log('Failed to send progress updates:', error);
      // Keep pending updates for retry later
    }
  };

  // Send progress when video ends
  const handleVideoEnded = async (videoId) => {
    console.log('handleVideoEnded called with videoId:', videoId);
    
    if (!videoId || videoId === 'unknown') {
      console.log('Invalid videoId, returning early');
      return;
    }
    
    // Get the latest progress from state (which has the most recent data)
    const latestProgress = videoProgress[videoId];
    const pendingUpdate = pendingProgressUpdates[videoId];
    
    console.log('Progress data:', { latestProgress, pendingUpdate });
    
    // Use latest progress or pending update, prioritizing pending
    const progressToSend = pendingUpdate || (latestProgress ? {
      current_time: latestProgress.currentTime,
      duration: latestProgress.duration,
      progress_percentage: latestProgress.progress,
      completed: latestProgress.progress >= 95
    } : null);
    
    console.log('Progress to send:', progressToSend);
    
    if (progressToSend) {
      try {
        // Force completion status for ended video
        const finalUpdate = {
          ...progressToSend,
          completed: true,
          progress_percentage: 100
        };
        console.log('Sending final update:', finalUpdate);
        await learnerProgressAPI.updateVideoProgress(videoId, finalUpdate);
        
        // Immediately mark video as completed in UI
        setCompletedItems(prev => {
          const newSet = new Set(prev);
          newSet.add(`video_${videoId}`);
          return newSet;
        });
        
        // Remove this video from pending updates
        setPendingProgressUpdates(prev => {
          const updated = { ...prev };
          delete updated[videoId];
          return updated;
        });
        console.log(`Sent final progress for video ${videoId} on video end`);
      } catch (error) {
        console.error('Failed to send progress on video end:', error);
      }
    } else {
      // Fallback: send completion with video duration if available
      const video = course?.videos?.find(v => v.id === videoId);
      console.log('No progress data, trying fallback with video:', video);
      if (video && video.duration) {
        try {
          const fallbackUpdate = {
            current_time: video.duration,
            duration: video.duration,
            progress_percentage: 100,
            completed: true
          };
          console.log('Sending fallback update:', fallbackUpdate);
          await learnerProgressAPI.updateVideoProgress(videoId, fallbackUpdate);
          
          // Immediately mark video as completed in UI
          setCompletedItems(prev => {
            const newSet = new Set(prev);
            newSet.add(`video_${videoId}`);
            return newSet;
          });
          
          console.log(`Sent final progress for video ${videoId} on video end (fallback)`);
        } catch (error) {
          console.error('Failed to send progress on video end:', error);
        }
      } else {
        console.log('No video found or no duration for fallback');
      }
    }
    
    // Also send any other pending updates
    console.log('Sending other pending updates...');
    await sendPendingProgressUpdates();
  };

  const handleVideoClose = async () => {
    console.log('handleVideoClose called');
    
    // Ensure current video's progress is captured and sent
    if (selectedVideo && selectedVideo.id) {
      const videoId = selectedVideo.id;
      const latestProgress = videoProgress[videoId];
      const pendingUpdate = pendingProgressUpdates[videoId];
      
      console.log('Close - Progress data:', { videoId, latestProgress, pendingUpdate });
      
      // Send pending update if available, otherwise send latest progress
      if (pendingUpdate) {
        console.log('Has pending update, will be sent by sendPendingProgressUpdates');
        
        // Check if video should be marked as completed based on progress
        if (pendingUpdate.progress_percentage >= 95) {
          setCompletedItems(prev => {
            const newSet = new Set(prev);
            newSet.add(`video_${videoId}`);
            return newSet;
          });
        }
      } else if (latestProgress) {
        try {
          const closeUpdate = {
            current_time: latestProgress.currentTime,
            duration: latestProgress.duration,
            progress_percentage: latestProgress.progress,
            completed: latestProgress.progress >= 95
          };
          console.log('Sending progress on pause:', closeUpdate);
          await learnerProgressAPI.updateVideoProgress(videoId, closeUpdate);
          
          // Mark as completed in UI if progress is sufficient
          if (latestProgress.progress >= 95) {
            setCompletedItems(prev => {
              const newSet = new Set(prev);
              newSet.add(`video_${videoId}`);
              return newSet;
            });
          }
          
          console.log(`Sent progress for video ${videoId}`);
        } catch (error) {
          console.error('Failed to send progress:', error);
        }
      } else {
        console.log('No progress data available');
      }
    } else {
      console.log('No selectedVideo or video.id');
    }
    
    // Send all pending progress updates
    console.log('Sending pending updates...');
    await sendPendingProgressUpdates();
    // Note: We don't clear selectedVideo in embedded mode - the video stays visible
  };

  const isItemCompleted = (itemType, itemId) => {
    return completedItems.has(`${itemType}_${itemId}`);
  };

  const handleRateCourse = async () => {
    try {
      setSubmitting(true);
      await learnerCourseAPI.rateCourse(courseId, ratingForm.rating, ratingForm.review);
      showToast('success', 'Rating submitted successfully!');
      setShowRatingModal(false);
      setRatingForm({ rating: 5, review: '' });
      fetchCourseDetails(); // Refresh to get updated rating
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFlagCourse = async () => {
    if (!flagForm.reason.trim() || flagForm.reason.length < 10) {
      showToast('error', 'Please provide a detailed reason (minimum 10 characters)');
      return;
    }
    
    try {
      setSubmitting(true);
      await learnerCourseAPI.flagCourse(courseId, flagForm.reason);
      showToast('success', 'Content flagged successfully. Our team will review it.');
      setShowFlagModal(false);
      setFlagForm({ reason: '' });
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to flag content');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateCourseProgress = () => {
    if (!course) return 0;
    
    // Use backend progress data if available
    if (course.overall_progress !== undefined) {
      return Math.round(course.overall_progress);
    }
    
    // Fallback to local calculation
    const totalItems = (course.videos?.length || 0) + (course.files?.length || 0) + (course.quizzes?.length || 0);
    if (totalItems === 0) return 0;
    
    return Math.round((completedItems.size / totalItems) * 100);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
            <FiStar
            key={star}
              size={16}
              color={star <= rating ? '#F59E0B' : '#E5E7EB'}
              fill={star <= rating ? '#F59E0B' : 'none'}
            />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading course details..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <EmptyState>
            <FiBook size={48} />
            <h3>Course not found</h3>
            <p>The course you're looking for doesn't exist or has been removed</p>
            <Button onClick={() => navigate('/learner/courses')}>
              Back to My Courses
            </Button>
          </EmptyState>
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  const courseProgress = calculateCourseProgress();

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <CourseDetailWrapper>
        {/* Back Button - Outside of header */}
        <div style={{ marginBottom: '16px' }}>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/learner/courses')}
            >
            <FiArrowLeft /> Back to My Courses
            </Button>
        </div>

        {/* Embedded Video Player Section - Always visible when video is selected */}
        {selectedVideo && (
          <VideoPlayerSection>
            <VideoViewer
              isOpen={true}
              embedded={true}
              onClose={handleVideoClose}
              video={selectedVideo}
              onDownload={(videoId) => handleDownload('video', videoId)}
              onProgress={handleVideoProgress}
              onVideoEnded={handleVideoEnded}
              initialTime={selectedVideo.progress?.current_time || videoProgress[selectedVideo.id]?.currentTime || 0}
              onNext={handleNextContent}
              onPrevious={handlePreviousContent}
              hasNext={currentItemIndex < contentItems.length - 1}
              hasPrevious={currentItemIndex > 0}
            />
          </VideoPlayerSection>
        )}

        {/* Course Header - Hidden when video is playing */}
        {!selectedVideo && (
          <>
            <CourseHeader>
              <CourseHeaderContent>
                <CourseThumbnailLarge>
                  {course.thumbnail_url ? (
                    <img src={course.thumbnail_url} alt={course.title} />
                  ) : (
                    <div className="no-thumbnail">
                      <FiBook size={64} />
                    </div>
                  )}
                </CourseThumbnailLarge>
                
                <div style={{ flex: 1 }}>
                  <CourseTitle>{course.title}</CourseTitle>
                  <CourseInstructor>
                    <FiUser /> Instructor: {course.teacher?.username || 'Unknown'}
                  </CourseInstructor>
                  
                  <CourseRating>
                    {renderStars(course.average_rating || 0)}
                    <span>{course.average_rating?.toFixed(1) || 'N/A'}</span>
                    <span>({course.rating_count || 0} ratings)</span>
                  </CourseRating>
                  
                  <CourseStats>
                    <div><FiBook /> {course.grade?.name || 'Unknown Grade'}</div>
                    <div><FiVideo /> {course.videos?.length || 0} videos</div>
                    <div><FiFileText /> {course.files?.length || 0} files</div>
                    <div><FiClock /> {course.term || 'N/A'}</div>
                    {course.price !== undefined && (
                      <div style={{ 
                        fontSize: '20px', 
                        fontWeight: '700', 
                        color: theme.colors.primary 
                      }}>
                        <FiDollarSign /> KES {course.price}
                      </div>
                    )}
                  </CourseStats>
                  
                  {!ownsCourse(courseId) && (
                    <Button 
                      onClick={() => setShowPurchaseModal(true)}
                      style={{ 
                        marginTop: '16px',
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                      }}
                    >
                      <FiDollarSign /> Purchase Course - KES {course.price || 0}
                    </Button>
                  )}
                </div>
              </CourseHeaderContent>
            </CourseHeader>

            {/* Progress Bar */}
            <ProgressBarContainer>
              <ProgressText>
                {courseProgress}% Complete
                {course.completed_count !== undefined && course.total_items !== undefined && (
                  <span style={{ 
                    fontSize: '14px', 
                    color: theme.colors.textSecondary,
                    marginLeft: '8px'
                  }}>
                    ({course.completed_count}/{course.total_items} items)
                  </span>
                )}
              </ProgressText>
              <div style={{ flex: 1, position: 'relative' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: theme.colors.border,
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <ProgressBarFill progress={courseProgress} />
                </div>
              </div>
            </ProgressBarContainer>
          </>
        )}

        {/* Main Content Area - Full width when video is playing, side by side otherwise */}
        <CourseContentSection fullWidth={!!selectedVideo}>
          {/* Course Curriculum - Full width when video is playing */}
          <CourseSidebar style={{ position: 'static' }}>
            <h3>Course Content</h3>
            
            {/* Progress Bar - Show when video is playing */}
            {selectedVideo && (
              <ProgressBarContainer style={{ marginBottom: '20px' }}>
                <ProgressText>
                  {courseProgress}% Complete
                  {course.completed_count !== undefined && course.total_items !== undefined && (
                    <span style={{ 
                      fontSize: '14px', 
                      color: theme.colors.textSecondary,
                      marginLeft: '8px'
                    }}>
                      ({course.completed_count}/{course.total_items} items)
                    </span>
                  )}
                </ProgressText>
                <div style={{ flex: 1, position: 'relative' }}>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: theme.colors.border,
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <ProgressBarFill progress={courseProgress} />
                  </div>
                </div>
              </ProgressBarContainer>
            )}
            
            {/* Videos Section */}
            {course.videos && course.videos.length > 0 && (
                <SectionItem>
                  <SectionItemHeader 
                    onClick={() => toggleSection('videos')}
                    expanded={expandedSections['videos']}
                  >
                    <div>
                      <FiVideo />
                      <span>Video Lessons</span>
                      <span className="count">({course.videos.length})</span>
                    </div>
                    {expandedSections['videos'] ? <FiChevronUp /> : <FiChevronDown />}
                  </SectionItemHeader>
                  
                  {expandedSections['videos'] && (
                    <SectionItemContent>
                      {course.videos.map((video) => {
                        const isCompleted = video.progress?.completed || isItemCompleted('video', video.id);
                        const progress = video.progress || videoProgress[video.id];
                        const hasAttachedFiles = course.files?.some(
                          f => f.course_video_id === video.id
                        );
                        
                        return (
                          <div key={video.id}>
                            <LessonItem 
                              onClick={() => handleWatchVideo(video)}
                              completed={isCompleted}
                              active={selectedVideo?.id === video.id}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                <CompletionCheckbox
                                  checked={isCompleted}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItemCompletion('video', video.id);
                                  }}
                                >
                                  {isCompleted && <FiCheck size={14} />}
                                </CompletionCheckbox>
                                
                                <LessonIcon completed={isCompleted}>
                                  <FiPlay />
                                </LessonIcon>
                                
                                <LessonTitle completed={isCompleted}>
                                  {video.title}
                                  {hasAttachedFiles && (
                                    <span className="file-badge">
                                      <FiFileText size={12} /> Files included
                                    </span>
                                  )}
                                </LessonTitle>
                              </div>
                              
                              <LessonDuration>
                                {progress ? (
                                  <span style={{ color: theme.colors.primary, fontSize: '12px' }}>
                                    {Math.round(progress.progress_percentage || progress.progress)}%
                                  </span>
                                ) : null}
                                <FiClock size={14} />
                                {formatDuration(video.duration)}
                              </LessonDuration>
                            </LessonItem>
                            
                            {/* Attached Files */}
                            {hasAttachedFiles && (
                              <div style={{ paddingLeft: '56px', marginTop: '4px' }}>
                                {course.files
                                  .filter(f => f.course_video_id === video.id)
                                  .map(file => {
                                    const fileCompleted = file.completed || isItemCompleted('file', file.id);
                                    return (
                                      <LessonItem
                                        key={file.id}
                                        onClick={() => handlePreviewFile(file)}
                                        completed={fileCompleted}
                                        style={{ fontSize: '13px', padding: '8px 12px' }}
                                      >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                          <CompletionCheckbox
                                            checked={fileCompleted}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleItemCompletion('file', file.id);
                                            }}
                                          >
                                            {fileCompleted && <FiCheck size={12} />}
                                          </CompletionCheckbox>
                                          
                                          <FiFileText size={14} />
                                          <span>{file.title}</span>
                                        </div>
                                      </LessonItem>
                                    );
                                  })}
          </div>
                            )}
          </div>
                        );
                      })}
                    </SectionItemContent>
                  )}
                </SectionItem>
              )}

              {/* Standalone Files Section */}
              {course.files && course.files.filter(f => !f.course_video_id).length > 0 && (
                <SectionItem>
                  <SectionItemHeader 
                    onClick={() => toggleSection('files')}
                    expanded={expandedSections['files']}
                  >
                    <div>
                      <FiFileText />
                      <span>Course Materials</span>
                      <span className="count">
                        ({course.files.filter(f => !f.course_video_id).length})
                      </span>
                    </div>
                    {expandedSections['files'] ? <FiChevronUp /> : <FiChevronDown />}
                  </SectionItemHeader>
                  
                  {expandedSections['files'] && (
                    <SectionItemContent>
                      {course.files
                        .filter(f => !f.course_video_id)
                        .map((file) => {
                          const isCompleted = isItemCompleted('file', file.id);
                          
                          return (
                            <LessonItem 
                              key={file.id}
                        onClick={() => handlePreviewFile(file)}
                              completed={isCompleted}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                <CompletionCheckbox
                                  checked={isCompleted}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItemCompletion('file', file.id);
                                  }}
                                >
                                  {isCompleted && <FiCheck size={14} />}
                                </CompletionCheckbox>
                                
                                <LessonIcon completed={isCompleted}>
                                  <FiFileText />
                                </LessonIcon>
                                
                                <LessonTitle completed={isCompleted}>
                                  {file.title}
                                </LessonTitle>
          </div>
                              
                              <LessonDuration>
                                {(file.file_size / 1024 / 1024).toFixed(1)} MB
                              </LessonDuration>
                            </LessonItem>
                          );
                        })}
                    </SectionItemContent>
                  )}
                </SectionItem>
              )}

            {/* Quizzes Section */}
            {course.quizzes && course.quizzes.length > 0 && (
              <SectionItem>
                <SectionItemHeader 
                  onClick={() => toggleSection('quizzes')}
                  expanded={expandedSections['quizzes']}
                >
                    <div>
                    <FiAward />
                    <span>Quizzes</span>
                    <span className="count">({course.quizzes.length})</span>
                    </div>
                  {expandedSections['quizzes'] ? <FiChevronUp /> : <FiChevronDown />}
                </SectionItemHeader>
                
                {expandedSections['quizzes'] && (
                  <SectionItemContent>
                    {course.quizzes.map((quiz) => {
                      const quizCompleted = quiz.completed || isItemCompleted('quiz', quiz.id);
                      const hasAttempts = quiz.attempt_summaries && quiz.attempt_summaries.length > 0;
                      const getBestScoreColor = (score) => {
                        if (score >= 80) return '#10B981';
                        if (score >= 60) return '#F59E0B';
                        return '#EF4444';
                      };
                      
                      return (
                        <div 
                          key={quiz.id}
                          style={{
                            padding: '16px',
                            background: theme.colors.surface,
                            borderRadius: '12px',
                            marginBottom: '12px',
                            border: `1px solid ${theme.colors.border}`
                          }}
                        >
                          {/* Quiz Header */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '12px',
                            marginBottom: hasAttempts ? '16px' : '0'
                          }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                              <div style={{
                                minWidth: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: `${theme.colors.primary}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.colors.primary
                              }}>
                                <FiAward size={20} />
                              </div>
                              
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontSize: '16px',
                                  fontWeight: '600',
                                  color: theme.colors.textPrimary,
                                  marginBottom: '4px'
                                }}>
                            {quiz.title}
                                </div>
                                <div style={{
                                  fontSize: '13px',
                                  color: theme.colors.textSecondary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  flexWrap: 'wrap'
                                }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <FiTarget size={12} />
                                    {quiz.question_count} questions
                                  </span>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <FiClock size={12} />
                                    {quiz.time_limit} min
                                  </span>
                                  {quiz.best_score !== null && quiz.best_score !== undefined && (
                                    <span style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      color: getBestScoreColor(quiz.best_score),
                                      fontWeight: '600'
                                    }}>
                                      <FiStar size={12} />
                                Best: {quiz.best_score}%
                              </span>
                      )}
                                  {quiz.attempts > 0 && (
                                    <span style={{
                                      padding: '2px 8px',
                                      background: theme.colors.backgroundSecondary,
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: '500'
                                    }}>
                                      {quiz.attempts} attempt{quiz.attempts !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                    </div>
          
                            {/* Take Quiz Button */}
                            <div style={{ marginLeft: 'auto' }}>
                              <Button
                                onClick={() => navigate(`/learner/quiz/${quiz.id}`)}
                                style={{
                                  padding: '8px 16px',
                                  fontSize: '14px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <FiPlay size={14} />
                                {hasAttempts ? 'Retake' : 'Take Quiz'}
                              </Button>
                            </div>
                          </div>

                          {/* Quiz Attempts */}
                          {hasAttempts && (
                            <div style={{
                              paddingTop: '16px',
                              borderTop: `1px solid ${theme.colors.border}`
                            }}>
                              <div style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                color: theme.colors.textSecondary,
                                marginBottom: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Your Attempts
                              </div>
                              
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {quiz.attempt_summaries
                                  .sort((a, b) => b.attempt_number - a.attempt_number)
                                  .map((attempt) => {
                                    const scoreColor = getBestScoreColor(attempt.percentage);
                                    const attemptDate = new Date(attempt.completed_at);
                                    const formattedDate = attemptDate.toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    });

                                    return (
                                      <div
                                        key={attempt.id}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '12px',
                                          padding: '10px 12px',
                                          background: theme.colors.backgroundSecondary,
                                          borderRadius: '8px',
                                          border: `1px solid ${attempt.passed ? '#10B98130' : '#EF444430'}`
                                        }}
                                      >
                                        {/* Attempt Number Badge */}
                                        <div style={{
                                          minWidth: '32px',
                                          height: '32px',
                                          borderRadius: '50%',
                                          background: scoreColor,
                                          color: 'white',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '13px',
                                          fontWeight: '600'
                                        }}>
                                          #{attempt.attempt_number}
                                        </div>

                                        {/* Score Info */}
                                        <div style={{ flex: 1 }}>
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '2px'
                                          }}>
                                            <span style={{
                                              fontSize: '15px',
                                              fontWeight: '600',
                                              color: scoreColor
                                            }}>
                                              {attempt.percentage.toFixed(1)}%
                                            </span>
                                            <span style={{
                                              fontSize: '13px',
                                              color: theme.colors.textSecondary
                                            }}>
                                              ({attempt.score} points)
                                            </span>
                                            {attempt.passed ? (
                                              <span style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: '12px',
                                                padding: '2px 6px',
                                                background: '#10B98115',
                                                color: '#10B981',
                                                borderRadius: '4px',
                                                fontWeight: '500'
                                              }}>
                                                <FiCheckCircle size={12} />
                                                Passed
                                              </span>
                                            ) : (
                                              <span style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: '12px',
                                                padding: '2px 6px',
                                                background: '#EF444415',
                                                color: '#EF4444',
                                                borderRadius: '4px',
                                                fontWeight: '500'
                                              }}>
                                                <FiXCircle size={12} />
                                                Failed
                                              </span>
                                            )}
                                          </div>
                                          <div style={{
                                            fontSize: '12px',
                                            color: theme.colors.textSecondary
                                          }}>
                                            {formattedDate}
                                          </div>
                                        </div>

                                        {/* Review Button */}
                                        <Button
                                          variant="ghost"
                                          onClick={() => navigate(`/learner/quiz/${quiz.id}/review/${attempt.id}`)}
                                          style={{
                                            padding: '6px 12px',
                                            fontSize: '13px',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          <FiTarget size={13} />
                                          Review
                                        </Button>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </SectionItemContent>
                )}
              </SectionItem>
            )}
          </CourseSidebar>

          {/* Right Panel - Course Information (Hidden when video is playing) */}
          {!selectedVideo && (
            <CourseContentPanel>
            <CourseAboutSection>
              <h2>About this course</h2>
              <CourseDescription>{course.description}</CourseDescription>
              
              <CourseInfoGrid>
                <CourseInfoItem>
                  <FiClock />
                  <div>
                    <strong>Duration</strong>
                    <span>{Math.floor((course.videos?.reduce((acc, v) => acc + (v.duration || 0), 0) || 0) / 60)} minutes</span>
                  </div>
                </CourseInfoItem>
                <CourseInfoItem>
                  <FiBook />
                  <div>
                    <strong>Grade Level</strong>
                    <span>{course.grade?.name || 'N/A'}</span>
                  </div>
                </CourseInfoItem>
                <CourseInfoItem>
                  <FiAward />
                  <div>
                    <strong>Pass Mark</strong>
                    <span>{course.pass_mark}%</span>
                  </div>
                </CourseInfoItem>
              </CourseInfoGrid>

              {/* Rating and Flag Actions */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: `1px solid ${theme.colors.border}`
              }}>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowRatingModal(true)}
                >
                  <FiStar /> Rate Course
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowFlagModal(true)}
                  style={{ color: '#EF4444' }}
                >
                  <FiFlag /> Flag Content
                </Button>
              </div>
            </CourseAboutSection>
          </CourseContentPanel>
          )}
        </CourseContentSection>
      </CourseDetailWrapper>

      {/* Transition Overlay */}
      {isTransitioning && (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          color: 'white',
          fontSize: '18px'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid #2c5f34',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>
          <div>Loading next content...</div>
        </div>
      )}

      {/* File Viewer (kept as modal for files) */}
      <FileViewer
        isOpen={showFileViewer}
        onClose={() => {
          setShowFileViewer(false);
          setSelectedFile(null);
        }}
        file={selectedFile}
        onNext={handleNextContent}
        onPrevious={handlePreviousContent}
        hasNext={currentItemIndex < contentItems.length - 1}
        hasPrevious={currentItemIndex > 0}
      />

      {/* Rating Modal */}
      <Modal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        title="Rate This Course"
        size="medium"
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '500',
              color: theme.colors.textPrimary 
            }}>
              Rating
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRatingForm({ ...ratingForm, rating: star })}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                >
                  <FiStar
                    size={32}
                    color={star <= ratingForm.rating ? '#F59E0B' : '#E5E7EB'}
                    fill={star <= ratingForm.rating ? '#F59E0B' : 'none'}
                  />
                </button>
              ))}
              <span style={{ 
                marginLeft: '12px',
                color: theme.colors.textSecondary,
                fontSize: '14px'
              }}>
                {ratingForm.rating} out of 5
              </span>
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '500',
              color: theme.colors.textPrimary 
            }}>
              Review (Optional)
            </label>
            <textarea
              value={ratingForm.review}
              onChange={(e) => setRatingForm({ ...ratingForm, review: e.target.value })}
              placeholder="Share your thoughts about this course..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit',
                color: theme.colors.textPrimary,
                background: theme.colors.surface,
              }}
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <Button 
              variant="ghost" 
              onClick={() => setShowRatingModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRateCourse} 
              loading={submitting}
            >
              Submit Rating
            </Button>
          </div>
        </div>
      </Modal>

      {/* Flag Modal */}
      <Modal
        isOpen={showFlagModal}
        onClose={() => setShowFlagModal(false)}
        title="Flag Inappropriate Content"
        size="medium"
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '500',
              color: theme.colors.textPrimary 
            }}>
              Reason for Flagging <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <textarea
              value={flagForm.reason}
              onChange={(e) => setFlagForm({ ...flagForm, reason: e.target.value })}
              placeholder="Please provide a detailed reason for flagging this content (minimum 10 characters)..."
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '12px',
                border: `2px solid ${flagForm.reason.length > 0 && flagForm.reason.length < 10 ? '#EF4444' : theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit',
                color: theme.colors.textPrimary,
                background: theme.colors.surface,
              }}
              required
            />
            <div style={{ 
              fontSize: '12px', 
              color: flagForm.reason.length < 10 ? '#EF4444' : theme.colors.textSecondary,
              marginTop: '4px'
            }}>
              {flagForm.reason.length}/10 characters minimum
            </div>
          </div>
          
          <div style={{ 
            padding: '12px',
            background: theme.colors.backgroundSecondary,
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <p style={{ 
              fontSize: '13px', 
              color: theme.colors.textSecondary,
              margin: 0,
              lineHeight: '1.5'
            }}>
              ⚠️ Please flag content only if it contains inappropriate material, inaccurate information, 
              or violates our community guidelines. Our team will review your report carefully.
            </p>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <Button 
              variant="ghost" 
              onClick={() => setShowFlagModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleFlagCourse} 
              loading={submitting}
              disabled={flagForm.reason.length < 10}
              style={{ background: '#EF4444' }}
            >
              Flag Content
            </Button>
          </div>
        </div>
      </Modal>

      {/* Purchase Course Modal */}
      <PurchaseCourseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        course={course}
        onSuccess={handlePurchaseSuccess}
      />
    </DashboardLayout>
  );
};

export default CourseDetailPage;
